<template>
  <div class="text-base font-normal leading-5">
    <div
      class="mb-2 flex items-center justify-between gap-4 border-b border-solid border-black-10 pb-1.5 leading-6 md:pb-2"
      :class="{
        'md:max-w-sm': isPdp,
      }"
    >
      <div v-if="formatedSizeCategories.length > 0" class="min-h-6">
        <div v-if="showMeasuringTape" class="flex gap-1">
          <IconMeasuringTape class="h-5 w-7 shrink-0" />
          <p class="text-base">
            {{ $t(sizeNote) }}
          </p>
        </div>
        <span v-else>{{ $t('pdp.size_picker.choose_size') }}:</span>
      </div>
      <span v-else class="inline-block min-h-6 min-w-px" aria-hidden="true" />
      <SizeGuide class="max-md:[&_svg]:hidden" />
    </div>
    <div>
      <ProductSizePickerTabs
        v-model="activeTab"
        :size-categories="formatedSizeCategories"
        :has-both-size-categories="hasBothSizeCategories"
      />
      <div class="mt-2">
        <div
          v-for="(category, idx) in formatedSizeCategories"
          v-show="activeTab === idx"
          :key="idx"
        >
          <div class="flex flex-wrap gap-2">
            <SizeVariant
              v-for="size in category.sizes"
              :key="size.id"
              :variant="size.variant"
              :active="
                selectedVariant && selectedVariant.id === size.variant.id
              "
              @select-size="handleSelectedSize"
            />
          </div>
        </div>
      </div>
    </div>
    <ProductSizeGuideModal
      v-if="isProductSizeGuideModalOpen"
      :product="product"
    />
  </div>
</template>

<script setup lang="ts">
import { watch, computed, ref } from 'vue'
import { isInStock } from '@scayle/storefront-nuxt'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import ProductSizePickerTabs from './ProductSizePickerTabs.vue'
import SizeVariant from './SizeVariant.vue'
import SizeGuide from './SizeGuide.vue'
import { getProductSizesFromVariants, getSizeNote } from '~/utils/product'
import useUiState from '~/composables/ui/useUiState'

const props = defineProps<{
  product: Product
  variants: Variant[]
  selectedVariant?: Variant
}>()

const emit = defineEmits<{
  (e: 'preSelectSize' | 'selectSize', variant: Variant): void
}>()

const { setActiveVariant, isProductSizeGuideModalOpen, isPdp } = useUiState()

const activeTab = ref(0)
const sizeNote = computed(() => getSizeNote(props.product))
const variantsInStock = computed((): Variant[] => {
  return props.variants.filter((variant) => isInStock(variant))
})
const sizeCategories = computed(() =>
  getProductSizesFromVariants(props.variants),
)

const formatedSizeCategories = computed(() =>
  Object.entries(sizeCategories.value).map(([categoryName, sizes]) => ({
    categoryName,
    sizes,
  })),
)

const hasBothSizeCategories = computed(() => {
  const names = formatedSizeCategories.value.map((c) => c.categoryName)
  return names.includes('ngroessen') && names.includes('kgroessen')
})

const showMeasuringTape = computed(() => !!sizeNote.value)

watch(
  variantsInStock,
  (newValue) => {
    // Automatically select variant if it is the only one in stock
    if (newValue.length === 1) {
      const variant = newValue[0]
      if (!variant) {
        return
      }
      setActiveVariant(variant)
      emit('preSelectSize', variant)
      emit('selectSize', variant)
    } else {
      setActiveVariant(undefined)
    }
  },
  { immediate: true },
)

watch(
  () => props.selectedVariant,
  (variant) => {
    if (!variant) {
      return
    }
    const tabIndex = formatedSizeCategories.value.findIndex((category) =>
      category.sizes.some((size) => size.variant.id === variant.id),
    )
    if (tabIndex !== -1) {
      activeTab.value = tabIndex
    }
  },
  { immediate: true },
)

const handleSelectedSize = (variant: Variant) => emit('selectSize', variant)
</script>
